import { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lotus paywall modal — surfaces when the user runs out of free interactions + credits,
 * OR when they click "Add lotuses" in the profile dropdown.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   reason: 'paywall' | 'topup' (controls headline only)
 */
const LotusPaywall = ({ open, onClose, reason = "paywall" }) => {
  const { sessionId } = useContext(EnlightenmentContext);
  const [packs, setPacks] = useState([]);
  const [loadingPackId, setLoadingPackId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/credits/packs`, { cache: "no-store" });
        const d = await r.json();
        if (alive) setPacks(d.packs || []);
      } catch { /* non-fatal — packs hard-coded below if fetch fails */ }
    })();
    return () => { alive = false; };
  }, [open]);

  const checkout = async (pack) => {
    setLoadingPackId(pack.id);
    try {
      const r = await fetch(`${API}/credits/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, pack_id: pack.id }),
      });
      const d = await r.json();
      if (d.url) {
        window.location.href = d.url;
      } else {
        alert(d.detail || "Could not start checkout. Please try again.");
      }
    } catch (err) {
      alert("Could not start checkout — please check your connection.");
    } finally {
      setLoadingPackId(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[450] flex items-center justify-center px-4"
          style={{ background: "rgba(14, 8, 24, 0.85)", backdropFilter: "blur(10px)" }}
          data-testid="lotus-paywall-modal"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ type: "spring", damping: 18, stiffness: 220 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-lg w-full rounded-3xl p-7"
            style={{
              background: "linear-gradient(135deg, #1A1030 0%, #2D1F50 55%, #1A2810 100%)",
              boxShadow: "0 40px 120px rgba(0,0,0,0.7), 0 0 80px rgba(212,165,116,0.3)",
              border: "1px solid rgba(212,165,116,0.4)",
            }}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10"
              style={{ color: "#D4A574" }}
              aria-label="Close"
              data-testid="lotus-paywall-close"
            >
              <X size={18} />
            </button>

            <div className="text-center mb-6">
              <div className="text-5xl mb-2" style={{ filter: "drop-shadow(0 0 16px rgba(212,165,116,0.6))" }}>
                {"\u{1FAB7}"}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-1"
                style={{ color: "#D4A574", fontFamily: "'Cinzel', serif" }}>
                Lotus Credits
              </div>
              <h2 className="text-2xl font-bold"
                style={{ color: "#FFF", fontFamily: "'Fredoka', sans-serif" }}>
                {reason === "topup" ? "Add more lotuses" : "Your free interactions are spent"}
              </h2>
              <p className="text-xs italic mt-2"
                style={{ color: "#B8A8D8", fontFamily: "'Nunito', sans-serif" }}>
                Each lotus is one conversation with Papaya. 100 lotuses ≈ 4 tiers of awakening.
              </p>
            </div>

            <div className="space-y-2.5 mb-3">
              {packs.map((pack) => (
                <motion.button
                  key={pack.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => checkout(pack)}
                  disabled={loadingPackId === pack.id}
                  className="w-full flex items-center justify-between rounded-2xl p-4 text-left transition"
                  style={{
                    background: "linear-gradient(135deg, rgba(212,165,116,0.15), rgba(123,94,167,0.18))",
                    border: "1px solid rgba(212,165,116,0.35)",
                  }}
                  data-testid={`lotus-pack-${pack.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{"\u{1FAB7}"}</div>
                    <div>
                      <div className="text-sm font-bold" style={{ color: "#FFF", fontFamily: "'Fredoka', sans-serif" }}>
                        {pack.credits} lotuses
                      </div>
                      <div className="text-[10px]" style={{ color: "#B8A8D8" }}>
                        ≈ {pack.interactions} interactions with Papaya
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold" style={{ color: "#D4A574", fontFamily: "'Fredoka', sans-serif" }}>
                      ${pack.price_usd.toFixed(2)}
                    </div>
                    {loadingPackId === pack.id && (
                      <div className="text-[10px]" style={{ color: "#B8A8D8" }}>opening Stripe…</div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>

            <div className="rounded-xl p-3 mt-4 text-center"
              style={{ background: "rgba(76,175,80,0.12)", border: "1px solid rgba(76,175,80,0.3)" }}
              data-testid="lotus-paywall-referral-hint">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1"
                style={{ color: "#7BC97B", fontFamily: "'Cinzel', serif" }}>
                <Sparkles size={11} className="inline -mt-0.5 mr-1" /> Free path
              </div>
              <p className="text-[11px] leading-snug" style={{ color: "#E8F0E8" }}>
                Share Primordeia with a friend — they get <strong>100 free lotuses</strong>,
                you get <strong>50 free lotuses</strong> the moment they sign up.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LotusPaywall;
